import './App.css';
import React, { lazy, useContext } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./api/queryClient";
import AuthState from "./context/App/AuthState";
import AuthContext from "./context/App/AuthContext";
import { ConfirmProvider } from "./context/ConfirmProvider";
import NotFound from './components/Common/NotFound';
import MainLayout from './components/Common/MainLayout';

// Public chunks
const Home = lazy(() => import('./components/Home/Home'));
const ContactUs = lazy(() => import('./components/ContactUs/ContactUs'));
const Login = lazy(() => import('./components/Auth/Login'));
const Signup = lazy(() => import('./components/Auth/Signup'));
const ForgotPassword = lazy(() => import('./components/Auth/ForgotPassword'));
const AuthLayout = lazy(() => import('./components/Auth/AuthLayout'));
const Service = lazy(() => import('./components/Services/Service'));
const Courses = lazy(() => import('./components/Courses/Courses'));
const Blog = lazy(() => import('./components/Blogs/Blog'));
const About = lazy(() => import('./components/About/About'));
const BlogSpecificPage = lazy(() => import('./components/Blogs/BlogSpecificPage'));

// Admin chunks (lazy so the public bundle stays small for visitors)
const ManageBlogs = lazy(() => import('./components/User/ManageBlog/ManageBlogs'));
const ManageReviews = lazy(() => import('./components/User/ManageReview/ManageReviews'));
const ManageServices = lazy(() => import('./components/User/ManageService/ManageServices'));
const ManageCourses = lazy(() => import('./components/User/ManageCourse/ManageCourses'));
const ManageHome = lazy(() => import('./components/User/ManageHome/ManageHome'));
const ManageAbout = lazy(() => import('./components/User/ManageAbout/ManageAbout'));
const ContactInfo = lazy(() => import('./components/ContactUs/ContactInfo'));
const Settings = lazy(() => import('./components/User/Settings/Settings'));

function PrivateRoute({ element }) {
  const { isAuthenticated } = useContext(AuthContext);
  return isAuthenticated ? element : <Navigate to="/login" />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfirmProvider>
        <Router>
          <AuthState>
            <Routes>
              {/* Main Application Routes (With Navbar & Footer) */}
              <Route element={<MainLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />

                {/* Auth Routes with Shared Layout for Transitions */}
                <Route element={<AuthLayout />}>
                  <Route path="/login" element={<Login />} />
                  <Route path="/signup" element={<Signup />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                </Route>

                <Route path="/contact" element={<ContactUs />} />
                <Route path="/services" element={<Service />} />
                <Route path="/courses" element={<Courses />} />
                <Route path="/blog" element={<Blog />}>
                  <Route path="/blog/:category/:title" element={<BlogSpecificPage />} />
                </Route>
                <Route path="/manageblog" element={<PrivateRoute element={<ManageBlogs />} />} />
                <Route path="/managereviews" element={<PrivateRoute element={<ManageReviews />} />} />
                <Route path="/manageservice" element={<PrivateRoute element={<ManageServices />} />} />
                <Route path="/managecourse" element={<PrivateRoute element={<ManageCourses />} />} />
                <Route path="/contactinfo" element={<PrivateRoute element={<ContactInfo />} />} />
                <Route path="/settings" element={<PrivateRoute element={<Settings />} />} />
                <Route path="/managehome" element={<PrivateRoute element={<ManageHome />} />} />
                <Route path="/manageabout" element={<PrivateRoute element={<ManageAbout />} />} />
              </Route>

              {/* 404 Catch-All (No Navbar/Footer) */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AuthState>
        </Router>
      </ConfirmProvider>
    </QueryClientProvider>
  );
}

export default App;
